/**
 * React Query hooks for the Critical Internal Function register (P429.1).
 *
 * GET  /v1/admin/twin/cifs                         the register
 * POST /v1/admin/twin/cifs                         raise a function
 * POST /v1/admin/twin/cifs/{id}/assessment         Stream C
 *
 * The challenge and the CRO / CIO signatures use the admission
 * endpoints every register row uses; they invalidate this register too.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import type {
  CifAssessmentIn,
  CifFunctionKind,
  CifRegister,
  CifRow,
} from "shared/api/types";

export const cifKeys = {
  register: () => ["cif", "register"] as const,
  services: () => ["cif", "services"] as const,
};

export function useCifRegister(): UseQueryResult<CifRegister, ApiError> {
  return useQuery({
    queryKey: cifKeys.register(),
    queryFn: () => api.get<CifRegister>("/v1/admin/twin/cifs"),
  });
}

export interface ServiceOption {
  id: string;
  code: string;
  name: string;
  /** Round 18 L13 -- so the internal tools can sit behind a toggle. */
  classification?: string | null;
  /** Round 19 L14 -- the bank's own mark of an internal tool. */
  internal_tool?: boolean | null;
}

export function useCifServiceOptions(
  enabled: boolean,
): UseQueryResult<ServiceOption[], ApiError> {
  return useQuery({
    queryKey: cifKeys.services(),
    queryFn: () => api.get<ServiceOption[]>("/v1/admin/foundation/services?limit=500"),
    enabled,
  });
}

export interface CreateCifArgs {
  name: string;
  function_kind: CifFunctionKind;
  service_ids: string[];
  rationale: string;
}

/* Round 19 G41 -- the function was absent from the Resilience Lead's
   My Work just after she recorded it and present later: My Work is
   cached for its 60s poll, and recording the function did not tell it.
   A function raised or assessed refreshes the register and My Work. */
function invalidateCifViews(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: cifKeys.register() });
  void qc.invalidateQueries({ queryKey: ["my-work"] });
}

export function useCreateCif() {
  const qc = useQueryClient();
  return useMutation<CifRow, ApiError, CreateCifArgs>({
    mutationFn: (body) => api.post<CifRow>("/v1/admin/twin/cifs", body),
    onSuccess: () => invalidateCifViews(qc),
  });
}

export function useAssessCif() {
  const qc = useQueryClient();
  return useMutation<CifRow, ApiError, { cifId: string; body: CifAssessmentIn }>({
    mutationFn: ({ cifId, body }) =>
      api.post<CifRow>(`/v1/admin/twin/cifs/${cifId}/assessment`, body),
    onSuccess: () => invalidateCifViews(qc),
  });
}
